import React, { FC, ReactElement, useContext, useMemo } from 'react'
import { CONFIGURATION, ConfigurationTabFieldNames, CRM_TYPE_SCOPE_NAME } from '../Constants/FieldNames'
import { CRMModal } from '../../../../../../../i18n'
import FormRow from '../../../../../../React/Common/Components/FormRow'
import { CRMTypes, useCRMTypes } from '../../../../../Hooks/useCRMTypes'
import { SelectOption } from '../../../../../../React/Common/Components/Selects/Types'
import CLanguageHandler from '../../../../../../NonReact/Languages/LanguageHandler'
import LanguageContext from '../../../../../../React/Common/Contexts/LanguageContext'
import { ANY_VALUE_STRING_NUMERIC } from '../../../../../../React/Common/Constants/DefaultValues'
import { ConfigurableTableFieldForm } from '../../../../Molecules/ConfigurableTableFieldForm/ConfigurableTableFieldForm'
import { ConfigurableTableConfig, FormTypes } from '../../../../Molecules/ConfigurableTableFieldForm/Types/Types'
import { CRMServerResponse, OptionsType, TemplateField } from '../Types/Types'
import { ComboBox } from '../../../../../../React/Common/Components/ComboBox/ComboBox'
import { SelectFilterOption } from '../../../../Molecules/AdminFilters/SelectFilter'
import { IsVarEmpty, isVarNotEmpty } from '../../../../../../NonReact/utils/GlobalFunctions'
import { CRMTypesResponse } from '../../../../../../NonReact/common/Types/Types'

export type Props = {
    isViewOnly?: boolean
    currentCRMConfiguration?: Nullable<CRMServerResponse>
    selectedCRMTemplate?: Nullable<TemplateField[]>
}

export const ConfigurationTab: FC<Props> = ({
    isViewOnly,
    currentCRMConfiguration,
    selectedCRMTemplate
}: Props): ReactElement => {
    const language: CLanguageHandler = useContext(LanguageContext) as CLanguageHandler
    const [crmTypes]: CRMTypes = useCRMTypes()

    const mappedCrmTypesToOptions: SelectFilterOption[] =
        crmTypes?.map(
            (crmType: CRMTypesResponse): SelectFilterOption => ({
                label: crmType.crmTypeName,
                value: crmType.crmType
            })
        ) ?? []

    const config: Nullable<ConfigurableTableConfig[]> = useMemo(
        (): ConfigurableTableConfig[] =>
            isVarNotEmpty(selectedCRMTemplate)
                ? selectedCRMTemplate
                      // Include Config Schema and Common only
                      .filter(
                          (field: TemplateField): boolean =>
                              (field.name.startsWith('ConfigSchema.') || field.name.startsWith('Common.')) &&
                              Object.values(FormTypes).includes(field.type as FormTypes)
                      )
                      .map((field: TemplateField): ConfigurableTableConfig => {
                          switch (field.type) {
                              case FormTypes.STRING:
                                  return {
                                      name: field.name,
                                      label: field?.label,
                                      type: FormTypes.STRING,
                                      default: field?.default as Definable<string>,
                                      validations: field.validations
                                  }

                              case FormTypes.BOOLEAN:
                                  return {
                                      name: field.name,
                                      label: field?.label,
                                      type: FormTypes.BOOLEAN,
                                      default: field?.default as Definable<boolean>,
                                      validations: field.validations
                                  }

                              case FormTypes.INTEGER:
                                  return {
                                      name: field.name,
                                      label: field?.label,
                                      type: FormTypes.INTEGER,
                                      default: field?.default as Definable<number>,
                                      validations: field.validations
                                  }

                              case FormTypes.NUMBER:
                                  return {
                                      name: field.name,
                                      label: field?.label,
                                      type: FormTypes.NUMBER,
                                      default: field?.default as Definable<number>,
                                      validations: field.validations
                                  }

                              case FormTypes.SELECT:
                                  return {
                                      name: field.name,
                                      label: field?.label,
                                      type: FormTypes.SELECT,
                                      options:
                                          field.options?.map(
                                              (option: OptionsType): SelectOption<string> => ({
                                                  label: option.label,
                                                  value: option.value
                                              })
                                          ) ?? [],
                                      default: field?.default as Definable<string>,
                                      validations: field.validations
                                  }

                              case FormTypes.ARRAY:
                                  return {
                                      name: field.name,
                                      label: field?.label,
                                      type: FormTypes.ARRAY,
                                      arrayFieldName: field.name.split('.').pop() || field.name,
                                      default: ((field?.default as Definable<string[]>) ?? []).map(
                                          (item: string): { value: string } => ({
                                              value: item
                                          })
                                      ),
                                      validations: field.validations
                                  }

                              case FormTypes.PASSWORD:
                                  return {
                                      name: field.name,
                                      label: field?.label,
                                      type: FormTypes.PASSWORD,
                                      default: field?.default as Definable<string>,
                                      validations: field.validations
                                  }

                              case FormTypes.MULTILINE:
                                  return {
                                      name: field.name,
                                      label: field?.label,
                                      type: FormTypes.MULTILINE,
                                      default: field?.default as Definable<string>,
                                      validations: field.validations
                                  }

                              default:
                                  throw new Error(`Unsupported field type: ${field.type}`)
                          }
                      })
                : [],
        [JSON.stringify(selectedCRMTemplate)]
    )

    return (
        <div className='tab-area'>
            <FormRow>
                <ComboBox
                    name={CRM_TYPE_SCOPE_NAME}
                    defaultOption={{
                        label: language.get(CRMModal.CRMConfigurationTypeNone),
                        value: ANY_VALUE_STRING_NUMERIC
                    }}
                    options={mappedCrmTypesToOptions}
                    label={language.get(CRMModal.CRMConfigurationCRMTypeLabel)}
                    variant='outlined'
                    disabled={isViewOnly || isVarNotEmpty(currentCRMConfiguration)}
                />
            </FormRow>
            {isVarNotEmpty(selectedCRMTemplate) &&
            selectedCRMTemplate.length > 0 &&
            isVarNotEmpty(config) &&
            config.length > 0 ? (
                <ConfigurableTableFieldForm
                    namespacingFunction={(name: string): string => `${CONFIGURATION}.${name}`}
                    localisationFunction={(name: string): string => language.get(`CRMIntegration_Options_${name}`)}
                    arrayFieldName={ConfigurationTabFieldNames.CRM_OPTIONS}
                    inputConfig={config}
                    isViewOnly={isViewOnly}
                    isAdding={IsVarEmpty(currentCRMConfiguration)}
                    localisation={{
                        header: {
                            item: language.get(CRMModal.CRMConfigurationTab_Setting_Name),
                            value: language.get(CRMModal.CRMConfigurationTab_Value)
                        }
                    }}
                    shouldScrollToTheTop={true}
                />
            ) : (
                <></>
            )}
        </div>
    )
}
